// Shared helpers for the Shoots module — jsPDF builders + WhatsApp share URL.
// Keeps NewShoot.js small and lets ShootDetail (future) reuse the same PDF code.
import { jsPDF } from "jspdf";

const R = { red: [225, 29, 46], white: [255, 255, 255], black: [10, 10, 12], mute: [140, 140, 140] };

const slug = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const groupByCategory = (items = []) => {
  const map = new Map();
  items.forEach((it) => {
    if (!map.has(it.category)) map.set(it.category, []);
    map.get(it.category).push(it);
  });
  return [...map.entries()];
};

/** Build the dark-themed Pre-shoot PDF. Returns nothing — triggers download. */
export function buildShootPdf(shoot) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 12;
  const paint = (fn) => fn(doc);

  // Dark background + red rails
  paint((d) => {
    d.setFillColor(...R.black);
    d.rect(0, 0, W, H, "F");
    d.setFillColor(...R.red);
    d.rect(0, 0, 4, H, "F");
    d.rect(W - 4, 0, 4, H, "F");
  });

  // Header
  let y = 18;
  doc.setTextColor(...R.white);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("LIFE INSIDE MEDIA", M, y);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...R.mute);
  doc.text("LIM FLOW · PUNE", M, y + 5);
  // REC badge
  doc.setFillColor(...R.red);
  doc.roundedRect(W - M - 46, y - 5, 34, 8, 1.2, 1.2, "F");
  doc.setTextColor(...R.white);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("REC · PRE-SHOOT", W - M - 44, y + 0.8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...R.mute);
  doc.setFontSize(7);
  doc.text(new Date().toLocaleString("en-IN"), W - M - 46, y + 6);

  // Divider
  y = 30;
  doc.setDrawColor(...R.red);
  doc.setLineWidth(0.5);
  doc.line(M, y, W - M, y);
  y += 8;

  // Meta rows
  const meta = [
    ["Client", shoot.client || "—"],
    ["Date", `${fmtDate(shoot.shoot_date)}${shoot.call_time ? "  ·  " + shoot.call_time : ""}`],
    ["Location", `${shoot.location_type || "—"}  ·  ${shoot.location || "—"}`],
    ["Shoot type", shoot.shoot_type || "—"],
    ["Crew", (shoot.crew || []).map((c) => c.name + (c.is_manager ? " (MGR)" : "")).join(", ") || "—"],
    ["Items", `${(shoot.items || []).reduce((n, it) => n + (it.qty || 1), 0)} in ${(shoot.items || []).length} lines`],
  ];
  doc.setFontSize(9);
  meta.forEach(([k, v]) => {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...R.mute);
    doc.text(k.toUpperCase(), M, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...R.white);
    const lines = doc.splitTextToSize(String(v), W - M * 2 - 30);
    doc.text(lines, M + 30, y);
    y += 5 + (lines.length - 1) * 4;
  });

  // Rental Equipment box (highlighted)
  if ((shoot.rental_equipment || "").trim()) {
    y += 4;
    const boxLines = doc.splitTextToSize(shoot.rental_equipment.trim(), W - M * 2 - 6);
    const boxH = 10 + boxLines.length * 4.6;
    doc.setDrawColor(...R.red);
    doc.setFillColor(30, 15, 18);
    doc.roundedRect(M, y, W - M * 2, boxH, 1.5, 1.5, "FD");
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...R.red);
    doc.setFontSize(8);
    doc.text("RENTAL EQUIPMENT", M + 4, y + 5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...R.white);
    doc.setFontSize(9);
    doc.text(boxLines, M + 4, y + 10);
    y += boxH + 4;
  }

  // Notes
  if ((shoot.notes || "").trim()) {
    y += 2;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...R.mute);
    doc.setFontSize(8);
    doc.text("NOTES", M, y);
    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...R.white);
    doc.setFontSize(9);
    const nl = doc.splitTextToSize(shoot.notes.trim(), W - M * 2);
    doc.text(nl, M, y);
    y += nl.length * 4.3 + 4;
  }

  // Divider
  y += 2;
  doc.setDrawColor(...R.red);
  doc.line(M, y, W - M, y);
  y += 6;

  // Equipment groups
  const grouped = groupByCategory(shoot.items || []);
  const ensureRoom = (needed) => {
    if (y + needed > H - 18) {
      // Footer
      doc.setTextColor(...R.mute);
      doc.setFontSize(7);
      doc.text("LIM Flow · Life Inside Media · Pune", M, H - 6);
      doc.text(`p.${doc.internal.getNumberOfPages()}`, W - M - 10, H - 6);
      doc.addPage();
      doc.setFillColor(...R.black);
      doc.rect(0, 0, W, H, "F");
      doc.setFillColor(...R.red);
      doc.rect(0, 0, 4, H, "F");
      doc.rect(W - 4, 0, 4, H, "F");
      y = M + 4;
    }
  };
  grouped.forEach(([cat, list]) => {
    ensureRoom(14 + list.length * 5);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...R.red);
    doc.text(cat.toUpperCase(), M, y);
    y += 5;
    doc.setDrawColor(...R.mute);
    doc.setLineWidth(0.1);
    doc.line(M, y - 1, W - M, y - 1);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    list.forEach((it) => {
      ensureRoom(5);
      doc.setTextColor(...R.white);
      doc.text("• " + it.name, M + 2, y);
      doc.setTextColor(...R.red);
      doc.setFont("helvetica", "bold");
      doc.text(`× ${it.qty || 1}`, W - M - 14, y);
      doc.setFont("helvetica", "normal");
      y += 5;
    });
    y += 3;
  });

  // Footer on last page
  doc.setTextColor(...R.mute);
  doc.setFontSize(7);
  doc.text("LIM Flow · Life Inside Media · Pune", M, H - 6);
  doc.text(`p.${doc.internal.getNumberOfPages()}`, W - M - 10, H - 6);

  doc.save(`lim-flow-${slug(shoot.client)}-${shoot.shoot_date}.pdf`);
}

/** Build a WhatsApp share URL. Uses wa.me/?text= so it works with any device. */
export function shootWhatsAppUrl(shoot) {
  const lines = [];
  lines.push("*LIM Flow · Pre-shoot Checklist*");
  lines.push("");
  lines.push(`*Client:* ${shoot.client || "—"}`);
  lines.push(`*Date:* ${fmtDate(shoot.shoot_date)}${shoot.call_time ? "  ·  " + shoot.call_time : ""}`);
  lines.push(`*Location:* ${shoot.location_type || "—"} — ${shoot.location || "—"}`);
  if (shoot.shoot_type) lines.push(`*Type:* ${shoot.shoot_type}`);
  const crew = (shoot.crew || []).map((c) => c.name + (c.is_manager ? " (MGR)" : "")).join(", ");
  if (crew) lines.push(`*Crew:* ${crew}`);
  if ((shoot.rental_equipment || "").trim()) {
    lines.push("");
    lines.push("*Rental Equipment*");
    lines.push(shoot.rental_equipment.trim());
  }
  if ((shoot.notes || "").trim()) {
    lines.push("");
    lines.push("*Notes*");
    lines.push(shoot.notes.trim());
  }
  const grouped = groupByCategory(shoot.items || []);
  grouped.forEach(([cat, list]) => {
    lines.push("");
    lines.push(`*${cat}*`);
    list.forEach((it) => lines.push(`• ${it.name} × ${it.qty || 1}`));
  });
  lines.push("");
  lines.push("— Sent from LIM Flow · Life Inside Media");
  return `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
}

/** Studio Booking Slip PDF — white paper, red top strip. */
export function buildBookingSlipPdf(slip) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 15;

  // Red top strip
  doc.setFillColor(...R.red);
  doc.rect(0, 0, W, 12, "F");
  doc.setTextColor(...R.white);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("LIFE INSIDE MEDIA · LIM FLOW · STUDIO · PUNE", M, 8);

  // Title
  doc.setTextColor(...R.black);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Studio Booking Slip", W / 2, 26, { align: "center" });

  let y = 40;
  const row = (label, value) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...R.mute);
    doc.text(label.toUpperCase(), M, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...R.black);
    const lines = doc.splitTextToSize(String(value || "—"), W - M * 2 - 40);
    doc.text(lines, M + 40, y);
    y += 8 + (lines.length - 1) * 5;
  };
  row("Date", fmtDate(slip.date));
  row("Customer", slip.customer_name);
  row("Start time", slip.start_time);
  row("End time", slip.end_time);
  const hrs = ((slip.duration_minutes || 0) / 60).toFixed(2);
  row("Duration", `${hrs} h  (${slip.duration_minutes || 0} min)`);

  // Advance / Pending side by side
  const boxY = y + 2;
  doc.setDrawColor(...R.red);
  doc.setLineWidth(0.4);
  doc.roundedRect(M, boxY, (W - M * 2) / 2 - 3, 20, 2, 2, "S");
  doc.roundedRect(M + (W - M * 2) / 2 + 3, boxY, (W - M * 2) / 2 - 3, 20, 2, 2, "S");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...R.red);
  doc.text("ADVANCE", M + 4, boxY + 6);
  doc.text("PENDING", M + (W - M * 2) / 2 + 7, boxY + 6);
  doc.setFontSize(14);
  doc.setTextColor(...R.black);
  doc.text(`Rs. ${(slip.advance || 0).toLocaleString("en-IN")}`, M + 4, boxY + 15);
  doc.text(`Rs. ${(slip.pending || 0).toLocaleString("en-IN")}`, M + (W - M * 2) / 2 + 7, boxY + 15);
  y = boxY + 26;

  if ((slip.notes || "").trim()) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...R.red);
    doc.text("ADDITIONAL EQUIPMENT / NOTES", M, y);
    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...R.black);
    const nl = doc.splitTextToSize(slip.notes.trim(), W - M * 2);
    doc.text(nl, M, y + 3);
    y += nl.length * 5 + 8;
  }

  // Signature (bottom-right)
  const sigY = H - 55;
  doc.setDrawColor(...R.mute);
  doc.setLineWidth(0.2);
  doc.line(W - M - 70, sigY + 28, W - M, sigY + 28);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...R.mute);
  doc.text("CUSTOMER SIGNATURE", W - M - 70, sigY + 33);
  if (slip.signature_data_url) {
    try {
      doc.addImage(slip.signature_data_url, "PNG", W - M - 70, sigY, 70, 28);
    } catch (_) { /* invalid data-url — skip */ }
  }

  // Footer
  doc.setTextColor(...R.mute);
  doc.setFontSize(7);
  doc.text("LIM Flow · Life Inside Media · Pune", M, H - 8);

  doc.save(`lim-booking-${slug(slip.customer_name)}-${slip.date}.pdf`);
}

/** Compress an uploaded photo to <= 400 px, JPEG @ 0.82 quality. */
export async function resizePhotoToDataUrl(file) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const max = 400;
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", 0.82);
}
